// Company settings (ADR-0006): type, hand-written validation, key-by-key diff. Pure, no IO.
// Keys are the TOML keys (glossary §10); the stored `content` is the parsed file as is.
export const AUTHORIZATIONS = ["export_local_clearance", "authorised_consignor"] as const;
export type Authorization = (typeof AUTHORIZATIONS)[number];

export type Settings = {
  company: { name: string; authorizations: Authorization[] };
  tolerance: {
    r1_quantity_kg: number;
    r3_deviation_pct: number;
    r5_forecast_window_days: number;
    r6_outage_threshold_min: number;
  };
  camera_gate: Record<string, string>;
};

export type SettingsError = { key: string; message: string };
export type SettingsResult = { ok: true; settings: Settings } | { ok: false; errors: SettingsError[] };
export type SettingsChange = { key: string; old: unknown; new: unknown };

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);
const num = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);

/** Every error is listed with its key path; nothing is defaulted. */
export function validateSettings(raw: unknown): SettingsResult {
  const errors: SettingsError[] = [];
  const err = (key: string, message: string) => { errors.push({ key, message }); };
  // Required table with a fixed key set: reports missing and unknown keys; `check` reports bad values.
  const table = (name: string, keys: string[], check: (t: Record<string, unknown>) => void) => {
    const t = (raw as Record<string, unknown>)[name];
    if (t === undefined) return err(name, "zorunlu bölüm eksik");
    if (!isObj(t)) return err(name, "tablo olmalı");
    for (const k of Object.keys(t)) if (!keys.includes(k)) err(`${name}.${k}`, "bilinmeyen anahtar");
    for (const k of keys) if (t[k] === undefined) err(`${name}.${k}`, "zorunlu anahtar eksik");
    check(t);
  };
  if (!isObj(raw)) return { ok: false, errors: [{ key: "", message: "ayar bir tablo olmalı" }] };
  for (const k of Object.keys(raw)) if (!["company", "tolerance", "camera_gate"].includes(k)) err(k, "bilinmeyen anahtar");

  table("company", ["name", "authorizations"], (t) => {
    if (t.name !== undefined && (typeof t.name !== "string" || t.name.trim() === "")) err("company.name", "boş olmayan metin olmalı");
    const a = t.authorizations;
    if (a === undefined) return;
    if (!Array.isArray(a)) return err("company.authorizations", "liste olmalı");
    a.forEach((x, i) => {
      if (!(AUTHORIZATIONS as readonly unknown[]).includes(x)) err(`company.authorizations[${i}]`, `şunlardan biri olmalı: ${AUTHORIZATIONS.join(", ")}`);
      else if (a.indexOf(x) !== i) err(`company.authorizations[${i}]`, "tekrar");
    });
  });
  table("tolerance", ["r1_quantity_kg", "r3_deviation_pct", "r5_forecast_window_days", "r6_outage_threshold_min"], (t) => {
    if (t.r1_quantity_kg !== undefined && !(num(t.r1_quantity_kg) && t.r1_quantity_kg >= 0)) err("tolerance.r1_quantity_kg", "0 veya büyük sayı olmalı");
    if (t.r3_deviation_pct !== undefined && !(num(t.r3_deviation_pct) && t.r3_deviation_pct >= 0 && t.r3_deviation_pct <= 100)) err("tolerance.r3_deviation_pct", "0-100 arası sayı olmalı");
    for (const k of ["r5_forecast_window_days", "r6_outage_threshold_min"]) {
      if (t[k] !== undefined && !(Number.isInteger(t[k]) && (t[k] as number) > 0)) err(`tolerance.${k}`, "0'dan büyük tam sayı olmalı");
    }
  });
  const cg = raw.camera_gate;
  if (cg === undefined) err("camera_gate", "zorunlu bölüm eksik");
  else if (!isObj(cg)) err("camera_gate", "tablo olmalı");
  else {
    if (Object.keys(cg).length === 0) err("camera_gate", "en az bir satır olmalı");
    for (const [k, g] of Object.entries(cg)) if (typeof g !== "string" || g.trim() === "") err(`camera_gate.${k}`, "boş olmayan metin olmalı");
  }
  return errors.length ? { ok: false, errors } : { ok: true, settings: raw as unknown as Settings };
}

/** Leaf paths: scalars and lists are leaves (`camera_gate.KAM-07`, `company.authorizations`). */
function leaves(v: unknown, path: string, out: Map<string, unknown>) {
  if (isObj(v)) for (const [k, x] of Object.entries(v)) leaves(x, path ? `${path}.${k}` : k, out);
  else out.set(path, v);
}

/** Key-by-key difference old → new; an added or removed key shows `null` on the missing side. */
export function diffSettings(oldS: Settings | null, newS: Settings): SettingsChange[] {
  const a = new Map<string, unknown>(), b = new Map<string, unknown>();
  if (oldS) leaves(oldS, "", a);
  leaves(newS, "", b);
  const changes: SettingsChange[] = [];
  for (const key of [...new Set([...a.keys(), ...b.keys()])].sort()) {
    if (JSON.stringify(a.get(key)) !== JSON.stringify(b.get(key))) changes.push({ key, old: a.get(key) ?? null, new: b.get(key) ?? null });
  }
  return changes;
}

/** E10 ekran etiketleri (glossary §3, ui.md §4.7). Ham anahtar ekrana çıkmaz. */
export const AUTHORIZATION_LABEL: Record<Authorization, string> = {
  export_local_clearance: "İhracatta yerinde gümrükleme",
  authorised_consignor: "İzinli gönderici",
};
export const TOLERANCE_LABEL: Record<keyof Settings["tolerance"], { label: string; unit: "kg" | "%" | "gün" | "dk" }> = {
  r1_quantity_kg: { label: "R1 miktar farkı toleransı", unit: "kg" },
  r3_deviation_pct: { label: "R3 reçete sapma toleransı", unit: "%" },
  r5_forecast_window_days: { label: "R5 öngörü penceresi", unit: "gün" },
  r6_outage_threshold_min: { label: "R6 kamera kesinti eşiği", unit: "dk" },
};
